import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import {
  Stack,
  Drawer as MuiDrawer,
  useMediaQuery,
  Paper,
  Toolbar,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { READER_MENU_ITEMS } from './ReaderMenuUtils';
import ReaderToolbar from './ReaderToolbar';
import { getTabId, getTabPanelId } from './menuTabUtils';
import { useBookMenuContext } from '../BookMenuContext';
import DynamicMenuItemComponent from './DynamicMenuItemComponent';
import DrawerContentArea from '../Utilities/DrawerContentArea';
import { useTranslation } from 'react-i18next';

const drawerWidth = 320;

function getSidebarDrawerWidth(theme: Theme) {
  if (theme.breakpoints.up('lg')) {
    return drawerWidth;
  }

  // between md and lg
  return 0;
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: `calc(${getSidebarDrawerWidth(theme)}px + ${theme.spacing(8)})`,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 0,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: `calc(${getSidebarDrawerWidth(theme)}px + ${theme.spacing(8)})`,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

export default function SidebarReaderMenu() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { activeMenuView, selectMenuView } = useBookMenuContext();
  const [open, setOpen] = useState(false);

  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isDesktopOpen = useMemo(() => isDesktop && open, [isDesktop, open]);

  // Close the sidebar on mobile if it is open
  useEffect(() => {
    if (!isDesktop && open) {
      setOpen(false);
    }
  }, [isDesktop, open]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && activeMenuView) {
      selectMenuView(null);
      // Focus the menu item that was active
      const menuItem = document.getElementById(
        `sidebar-item-${activeMenuView}`,
      );
      menuItem?.focus();
    }
  };

  const handleDrawerToggle = (id: string) => {
    const shouldOpen = id && activeMenuView !== id;

    if (shouldOpen) {
      selectMenuView(id);
      setOpen(true);
      return;
    }

    setOpen(false);
    const leavingScreenDuration = theme.transitions.duration.leavingScreen;

    // Wait for the transition to finish before resetting the active menu view
    setTimeout(() => {
      selectMenuView(null);
    }, leavingScreenDuration ?? 0);
  };

  const drawerContent = READER_MENU_ITEMS.find(
    (item) => item.id === activeMenuView,
  );

  return (
    <Stack
      direction='row'
      component='aside'
      aria-label='Reading view sidebar'
      onKeyDown={handleKeyDown}
    >
      {isDesktop && (
        <>
          <ReaderToolbar
            variant='vertical'
            activeId={activeMenuView}
            onItemClick={handleDrawerToggle}
          />
          <Drawer
            variant='permanent'
            open={isDesktopOpen}
            sx={{}}
            slotProps={{
              paper: {
                sx: {
                  border: 'none',
                  backgroundColor: 'transparent',
                  left: `calc(${theme.spacing(8)})`,
                },
              },
            }}
          >
            <Stack
              id='sidebar-stack'
              direction='row'
              sx={{ overflowY: 'hidden', overflowX: 'hidden', height: '100%' }}
            >
              <Paper
                elevation={0}
                tabIndex={-1}
                sx={{
                  whiteSpace: 'wrap',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  '::-webkit-scrollbar': { display: 'none' },
                  maxWidth: (currentTheme) =>
                    getSidebarDrawerWidth(currentTheme),
                  width: '100%',
                }}
              >
                <Toolbar />
                <DrawerContentArea
                  title={
                    drawerContent?.labelKey ? t(drawerContent.labelKey) : ''
                  }
                  onClose={
                    activeMenuView
                      ? () => handleDrawerToggle(activeMenuView)
                      : undefined
                  }
                  titleId={drawerContent?.titleId}
                >
                  {READER_MENU_ITEMS.map((item) => (
                    <Stack
                      key={item.id}
                      id={getTabPanelId(item)}
                      role='tabpanel'
                      aria-labelledby={getTabId(item)}
                      tabIndex={0}
                      sx={{
                        ...(activeMenuView !== item.id && { display: 'none' }),
                      }}
                    >
                      <DynamicMenuItemComponent
                        component={item.content}
                        onClose={handleDrawerToggle}
                      />
                    </Stack>
                  ))}
                </DrawerContentArea>
              </Paper>
            </Stack>
          </Drawer>
        </>
      )}
    </Stack>
  );
}
